import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { 
  FaUsers, 
  FaLightbulb, 
  FaHandshake, 
  FaAward, 
  FaHeart,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaRocket,
  FaShieldAlt,
  FaStar,
  FaClock
} from 'react-icons/fa';

const AnimatedCard: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className = '' }) => {
  const { ref, isVisible } = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-10'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const About: React.FC = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const values = [
    { icon: FaRocket, title: t('about.value.1.title'), desc: t('about.value.1.desc') },
    { icon: FaHandshake, title: t('about.value.2.title'), desc: t('about.value.2.desc') },
    { icon: FaAward, title: t('about.value.3.title'), desc: t('about.value.3.desc') },
    { icon: FaHeart, title: t('about.value.4.title'), desc: t('about.value.4.desc') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-light-green-400 via-light-green-500 to-saudi-green-500 text-white py-20">
        <div className="absolute inset-0 opacity-5 bg-geometric-pattern"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-light-green-400/90 via-light-green-500/90 to-saudi-green-600/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl lg:text-6xl font-bold mb-4">
              {t('about.title')}
            </h1>
            <p className="text-2xl text-green-50 font-medium">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedCard>
              <div className="relative">
                <div className="h-96 bg-gradient-to-br from-light-green-400 via-light-green-500 to-saudi-green-500 rounded-2xl shadow-2xl flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-black opacity-10"></div>
                  <div className="absolute inset-0 bg-geometric-pattern opacity-5"></div>
                  <div className="relative z-10 text-center text-white p-8">
                    {/* @ts-expect-error - react-icons compatibility with React 19 */}
                    <FaGlobe className="mx-auto mb-4" size={80} />
                    <h3 className="text-3xl font-bold mb-2">Vision 2030</h3>
                    <p className="text-lg opacity-90">
                      {language === 'en' ? 'Sustainable Development' : 'التنمية المستدامة'}
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedCard>
            <AnimatedCard delay={100}>
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                  {t('about.vision.title')}
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {t('about.vision.desc')}
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-1 bg-gradient-to-r from-light-green-500 to-saudi-green-500 rounded-full"></div>
                  <div className="w-8 h-1 bg-gradient-to-r from-saudi-gold-400 to-saudi-gold-500 rounded-full"></div>
                </div>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gradient-to-b from-light-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                {t('about.mission.title')}
              </h2>
              <div className="max-w-4xl mx-auto">
                <p className="text-xl text-gray-700 leading-relaxed">
                  {t('about.mission.desc')}
                </p>
              </div>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <AnimatedCard delay={0}>
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center border-2 border-light-green-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaStar className="text-white" size={36} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Experience' : 'الخبرة'}
                </h3>
                <p className="text-gray-600">
                  {language === 'en' 
                    ? 'Years of expertise in travel and tourism'
                    : 'سنوات من الخبرة في السفر والسياحة'}
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard delay={100}>
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center border-2 border-light-green-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaShieldAlt className="text-white" size={36} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Professionalism' : 'الاحترافية'}
                </h3>
                <p className="text-gray-600">
                  {language === 'en'
                    ? 'Professional service delivery standards'
                    : 'معايير احترافية في تقديم الخدمات'}
                </p>
              </div>
            </AnimatedCard>
            <AnimatedCard delay={200}>
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center border-2 border-light-green-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  {/* @ts-expect-error - react-icons compatibility with React 19 */}
                  <FaGlobe className="text-white" size={36} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Kingdom Wide' : 'في جميع أنحاء المملكة'}
                </h3>
                <p className="text-gray-600">
                  {language === 'en'
                    ? 'Comprehensive services across Saudi Arabia'
                    : 'خدمات شاملة في جميع أنحاء المملكة'}
                </p>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedCard>
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                  {t('about.team.title')}
                </h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {t('about.team.desc')}
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-12 h-12 rounded-lg flex items-center justify-center">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <FaUsers className="text-white" size={20} />
                    </div>
                    <span className="text-gray-700 font-semibold">
                      {language === 'en' ? 'Multinational Professionals' : 'محترفون متعددو الجنسيات'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-12 h-12 rounded-lg flex items-center justify-center">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <FaGlobe className="text-white" size={20} />
                    </div>
                    <span className="text-gray-700 font-semibold">
                      {language === 'en' ? 'Multilingual Team' : 'فريق متعدد اللغات'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-12 h-12 rounded-lg flex items-center justify-center">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <FaLightbulb className="text-white" size={20} />
                    </div>
                    <span className="text-gray-700 font-semibold">
                      {language === 'en' ? 'Latest Technologies' : 'أحدث التقنيات'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-12 h-12 rounded-lg flex items-center justify-center">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <FaClock className="text-white" size={20} />
                    </div>
                    <span className="text-gray-700 font-semibold">
                      {language === 'en' ? '24/7 Support' : 'دعم على مدار الساعة'}
                    </span>
                  </div>
                </div>
              </div>
            </AnimatedCard>
            <AnimatedCard delay={100}>
              <div className="relative">
                <div className="h-96 bg-gradient-to-br from-saudi-gold-300 via-saudi-gold-400 to-saudi-gold-500 rounded-2xl shadow-2xl flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-black opacity-10"></div>
                  <div className="absolute inset-0 bg-geometric-pattern opacity-5"></div>
                  <div className="relative z-10 text-center text-white p-8">
                    {/* @ts-expect-error - react-icons compatibility with React 19 */}
                    <FaUsers className="mx-auto mb-4" size={80} />
                    <h3 className="text-3xl font-bold mb-2">
                      {language === 'en' ? 'Expert Team' : 'فريق خبير'}
                    </h3>
                    <p className="text-lg opacity-90">
                      {language === 'en' ? 'Dedicated Professionals' : 'محترفون مخلصون'}
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedCard>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {t('about.values.title')}
              </h2>
              <p className="text-xl text-gray-600">
                {language === 'en' 
                  ? 'The principles that guide everything we do'
                  : 'المبادئ التي توجه كل ما نقوم به'}
              </p>
            </div>
          </AnimatedCard>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <AnimatedCard key={index} delay={index * 100}>
                  <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-light-green-100 text-center">
                    <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                      {/* @ts-expect-error - react-icons compatibility with React 19 */}
                      <Icon className="text-white" size={36} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {value.desc}
                    </p>
                  </div>
                </AnimatedCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedCard>
            <div className="bg-gradient-to-br from-light-green-50 via-green-50 to-emerald-50 rounded-2xl p-12 border-2 border-light-green-200">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-12 text-center">
                {t('about.contact.title')}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                    {/* @ts-expect-error - react-icons compatibility with React 19 */}
                    <FaPhone className="text-white" size={24} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    {language === 'en' ? 'Phone' : 'الهاتف'}
                  </h3>
                  <p className="text-light-green-600 font-semibold">+966500446635</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                    {/* @ts-expect-error - react-icons compatibility with React 19 */}
                    <FaEnvelope className="text-white" size={24} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    {language === 'en' ? 'Email' : 'البريد الإلكتروني'}
                  </h3>
                  <p className="text-light-green-600 font-semibold text-sm">reservation@alnukhbah.com.sa</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md text-center">
                  <div className="bg-gradient-to-br from-light-green-400 to-light-green-500 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                    {/* @ts-expect-error - react-icons compatibility with React 19 */}
                    <FaMapMarkerAlt className="text-white" size={24} />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    {t('about.location')}
                  </h3>
                  <p className="text-light-green-600 font-semibold text-sm">{t('about.location.desc')}</p>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t-2 border-light-green-200 text-center">
                <p className="text-gray-700 mb-2">
                  <span className="font-bold">{t('about.taxId')}:</span> <span className="text-light-green-600 font-semibold">30007080345678</span>
                </p>
              </div>
            </div>
          </AnimatedCard>
        </div>
      </section>
    </div>
  );
};

export default About;
